"use client";

import { Music2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import {
  type MediaSoundPlacement,
  mediaSoundButtonClass,
  SoundToggleButton,
} from "@/components/ui/media-sound-button";
import type { MusicAttachment } from "@/lib/music/attachment";
import { safePreviewUrl } from "@/lib/music/links";
import { cn } from "@/lib/utils";
import { PlayingBars } from "./playing-bars";
import { SongSheet } from "./song-sheet";

/**
 * Instagram-style music on STATIC media (a post's photos, an event or
 * experience banner image).
 *
 * The song is an invisible, audio-only <video> laid over the image and
 * registered with the page-wide VideoAutoplayProvider — the same registry feed
 * and banner videos use. So it inherits, for free: autoplay when ~60% visible,
 * the ONE global "sound on" switch on phones (sound follows whatever is most
 * visible as you scroll), one owner on desktop, pausing under drawers and on
 * route change, and the iOS "unmuted play refused → muted" fallback. Turning
 * sound on also takes app-wide audio focus, so a song preview elsewhere stops.
 *
 * `active` false (a video slide is showing) unmounts the track, so the real
 * video owns the sound — music never plays over a video.
 *
 * Render inside a `relative` media frame, as a SIBLING of any media link or
 * button (the pill is its own button).
 */
export function MediaMusic({
  audioId,
  music,
  active = true,
  variant = "pill",
  splitControls = false,
  placement = "bottom-right",
  className,
  speakerClassName,
}: {
  /** Stable registry id, e.g. `post-12-music`. */
  audioId: string;
  music: MusicAttachment;
  /** False while a video slide owns the frame. */
  active?: boolean;
  /**
   * `pill`: a "♪ Title · Artist" credit (bottom-left; `className` moves it)
   * plus the standard speaker at `placement` (detail heroes).
   * `button`: the standard speaker alone, or a note that opens the song
   * when there is no playable preview (small cards).
   */
  variant?: "pill" | "button";
  /** Render only the speaker — the song is credited elsewhere (feed posts). */
  splitControls?: boolean;
  /** Corner of the speaker. Bottom-right unless the bottom is taken. */
  placement?: MediaSoundPlacement;
  /** The credit pill in `pill`, the speaker otherwise. */
  className?: string;
  /** The speaker in `pill`. */
  speakerClassName?: string;
}) {
  const { isMuted, isSoundMuted, register, toggleAudio } = useVideoAutoplay();
  const [sheetOpen, setSheetOpen] = useState(false);
  const src = safePreviewUrl(music.previewUrl);
  // The track stays MOUNTED while inactive and only leaves the registry. A
  // fresh element on every return to a photo is one iOS has never seen a
  // gesture on, so its unmuted play() is refused and the song comes back
  // silent; the same element keeps the unlock.
  const setTrackRef = useCallback(
    (element: HTMLVideoElement | null) =>
      register(audioId, active ? element : null),
    [active, audioId, register],
  );
  const canPlay = Boolean(src) && active;
  const audible = canPlay && !isMuted(audioId);
  const soundOff = isSoundMuted(audioId);

  const openSheet = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setSheetOpen(true);
  };

  return (
    <>
      {src ? (
        <video
          ref={setTrackRef}
          src={src as string}
          aria-hidden
          tabIndex={-1}
          playsInline
          loop
          preload="none"
          muted={isMuted(audioId)}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        />
      ) : null}

      {variant === "button" ? (
        canPlay ? (
          <SoundToggleButton
            muted={soundOff}
            onToggle={() => toggleAudio(audioId)}
            label="song"
            placement={placement}
            className={className}
          />
        ) : src ? null : (
          // No playable preview: same shell, same corner, opens the song.
          <button
            type="button"
            onClick={openSheet}
            aria-label={`${music.title} by ${music.artistName}`}
            className={cn(mediaSoundButtonClass(placement), className)}
          >
            <Music2 className="size-4" aria-hidden />
          </button>
        )
      ) : splitControls ? (
        canPlay ? (
          <SoundToggleButton
            muted={soundOff}
            onToggle={() => toggleAudio(audioId)}
            label="song"
            placement={placement}
            className={className}
          />
        ) : null
      ) : (
        <>
          <button
            type="button"
            onClick={openSheet}
            aria-label={`${music.title} by ${music.artistName} — song options`}
            className={cn(
              "absolute bottom-3 left-3 z-10 flex h-8 max-w-[min(60%,16rem)] items-center gap-1.5 rounded-full bg-black/55 px-2.5 text-white shadow-sm backdrop-blur-md transition-transform duration-200 ease-out-strong active:scale-95",
              className,
            )}
          >
            {audible ? (
              <PlayingBars className="h-3 shrink-0" barClassName="w-[2px]" />
            ) : (
              <Music2 className="size-3.5 shrink-0" />
            )}
            <span className="truncate text-xs font-semibold">
              {music.title}
              <span className="font-normal opacity-80">
                {" "}
                · {music.artistName}
              </span>
            </span>
          </button>
          {canPlay ? (
            <SoundToggleButton
              muted={soundOff}
              onToggle={() => toggleAudio(audioId)}
              label="song"
              placement={placement}
              className={speakerClassName}
            />
          ) : null}
        </>
      )}

      <SongSheet open={sheetOpen} onOpenChange={setSheetOpen} music={music} />
    </>
  );
}

export function MediaMusicCredit({
  audioId,
  music,
  active,
}: {
  audioId: string;
  music: MusicAttachment;
  active: boolean;
}) {
  const { isMuted } = useVideoAutoplay();
  const [sheetOpen, setSheetOpen] = useState(false);
  const playing =
    active && Boolean(safePreviewUrl(music.previewUrl)) && !isMuted(audioId);

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        aria-label={`${music.title} by ${music.artistName} — song options`}
        className="flex max-w-full min-w-0 items-center gap-1.5 text-left text-[13px] leading-[18px] text-text-primary transition-opacity hover:opacity-75"
      >
        {/* Bars while the song is audible, a note otherwise — the same
            "this is the one playing" cue the song cards use. */}
        <span className="flex size-3.5 shrink-0 items-center justify-center">
          {playing ? (
            <PlayingBars className="h-3" barClassName="w-[2px]" />
          ) : (
            <Music2 className="size-3.5" />
          )}
        </span>
        <span className="truncate">
          {music.title}
          <span className="text-text-secondary"> · {music.artistName}</span>
        </span>
      </button>
      <SongSheet open={sheetOpen} onOpenChange={setSheetOpen} music={music} />
    </>
  );
}
